import { NavLink } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
  ButtonSecondary,
  SidePanelRow,
  SidePanelLoader,
  Select,
  IconChangeLoClock,
  ButtonPseudo,
  IconRefresh,
  ButtonSize,
} from '@ui/src';
import { useAppSelector } from '@store';
import { CustomerStatus, MarginAmount } from '../../../hoc/customers/types';
import { MarginPanelContentProps } from './types';
import MarginAmountItem from './margin-amount-item';

import cn from './styles.module.css';

const MarginPanelContent = ({
  control,
  isLoading,
  errors,
  isEditable,
  amounts,
  setValue,
  isCustom,
  marginId,
  setAmounts,
  marginFeeError,
  auditLogAvailable,
  onResetMargins,
  userMargin,
  restoreInProgress,
  currencyFromId,
  status,
}: MarginPanelContentProps): React.JSX.Element => {
  const {
    customerTariffs: { rateSources },
    customersDictionaries: { currenciesList: currencies },
  } = useAppSelector((state) => state);

  let content: React.JSX.Element;

  if (isLoading) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        <div className={cn.blockWrapper}>
          <div className={cn.row}>
            <SidePanelRow caption="Currency From">
              <Controller
                control={control}
                name="currencyFromId"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    placeholder="Enter currency from..."
                    name={name}
                    value={value}
                    onChange={onChange}
                    selectWrapperClassName={cn.currencySelect}
                    list={currencies?.map((item) => ({
                      name: item.name,
                      value: item.id,
                    }))}
                    disabled
                    error={errors?.currencyFromId?.message}
                    fullWidth
                  />
                )}
              />
            </SidePanelRow>
            <SidePanelRow caption="Currency To">
              <Controller
                control={control}
                name="currencyToId"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    placeholder="Enter currency to..."
                    name={name}
                    value={value}
                    selectWrapperClassName={cn.currencySelect}
                    onChange={onChange}
                    list={currencies?.map((item) => ({
                      name: item.name,
                      value: item.id,
                    }))}
                    disabled
                    error={errors?.currencyFromId?.message}
                    fullWidth
                  />
                )}
              />
            </SidePanelRow>
          </div>
        </div>
        <div className={cn.marginTitle}>Set Rate type</div>
        <SidePanelRow>
          <Controller
            control={control}
            defaultValue=""
            name="rateSourceId"
            render={({ field: { onChange, value, name } }) => (
              <Select
                name={name}
                onChange={onChange}
                value={value}
                list={rateSources?.map((item) => ({
                  name: item.name,
                  value: item.id,
                }))}
                disabled={!isEditable || status === CustomerStatus.CLOSED}
                fullWidth
              />
            )}
          />
        </SidePanelRow>

        <div className={cn.marginTopWrapper}>
          <div className={cn.marginTitle}>Set FX margin</div>
          {isEditable && userMargin && status !== CustomerStatus.CLOSED && (
            <ButtonPseudo
              text="Restore defaults"
              icon={
                <IconRefresh className={restoreInProgress ? cn.rotate : ''} />
              }
              size={ButtonSize.SMALL}
              bold
              onClick={() => onResetMargins && onResetMargins(true)}
              disabled={restoreInProgress}
            />
          )}
        </div>
        <div>
          <div className={cn.feeRanges}>
            {amounts?.map((item, index) => (
              <MarginAmountItem
                key={item?.id}
                amount={item}
                index={index}
                amounts={amounts}
                setAmounts={setAmounts}
                isEditable={isEditable}
                setValue={setValue}
                isCustom={isCustom}
                marginId={marginId}
                marginFeeError={marginFeeError}
                currencies={currencies}
                currencyFromId={currencyFromId}
                status={status}
              />
            ))}
          </div>
          <Controller
            control={control}
            name="amounts"
            render={({ field: { name } }) => (
              <input type="hidden" name={name} />
            )}
          />
          {isEditable && status !== CustomerStatus.CLOSED && (
            <ButtonSecondary
              className={cn.feeRangeAdd}
              text="Add price range"
              onClick={() => {
                let nextAmounts = ([] as MarginAmount[]).concat(
                  amounts as MarginAmount[],
                );

                if (!amounts?.length) {
                  nextAmounts = [];
                }

                nextAmounts.push({
                  fromAmount: 0,
                  feePercent: 0,
                  id: `amount-${(amounts?.length as number) + 1}`,
                });

                setAmounts(nextAmounts);
              }}
            />
          )}
        </div>
        {auditLogAvailable && (
          <NavLink
            to={`/administration/change-logs?entityType=MARGIN&entityId=${marginId}`}
            target="_blank"
            className={cn.changeLogLink}
          >
            <IconChangeLoClock />
            Change Log
          </NavLink>
        )}
      </>
    );
  }

  return <div className={cn.sidepanel}>{content}</div>;
};

export default MarginPanelContent;
